import React, { useMemo, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Typography, Grid, Stack, Tooltip } from '@mui/material';
import { AddCircleOutlined, Help, RemoveCircle } from '@mui/icons-material';
import type { StepperFormContentValues } from './types';
import { IDENTITY_TYPE, IDENTITY_DOCUMENT_TYPE } from './constants';
import {
  genderOptions,
  identityTypeFullOptions,
  localTypeOptions,
  needStudentVisaOptions,
} from './options';
import {
  useAdminRegenerateVisa,
  useAdminDeleteVisa,
  useGetSRSList,
} from '../graphql';
import {
  Alert,
  type AlertDetails,
  Button,
  Dialog,
  type DialogDetails,
  IconButton,
  Loading,
} from '../../../components';
import {
  returnedComponent,
  type InputTextProps,
} from '../../Application/components/returnFieldComponent';
import { getApplicantConfig, VIEW_MODE } from '../../../utils';
import { PAGE_TEXT } from './lang';

export type ApplicantInfoFormContentProps = {
  formik: any;
  viewMode: VIEW_MODE;
};

const CHN = 'CHN';
const HKG = 'HKG';

export const ApplicantInfoFormContent: React.FC<
  ApplicantInfoFormContentProps
> = (props: ApplicantInfoFormContentProps) => {
  const { formik, viewMode } = props;

  const isSuperAdmin = viewMode === VIEW_MODE.SUPER_ADMIN;

  const isSignUp = viewMode === VIEW_MODE.SIGN_UP;

  const isPermanentHK = formik.values.identityType === IDENTITY_TYPE.HKID_PERM;

  const isChina = formik.values.nationality === CHN;

  const regenerateVisa = useAdminRegenerateVisa();
  const deleteVisa = useAdminDeleteVisa();

  const {
    programmeChoiceMetadata,
    loading: SRSLoading,
    error: SRSError,
  } = useGetSRSList();
  const { disabilityTypeList, provinceList, countryList } =
    programmeChoiceMetadata;
  const hkOption = useMemo(
    () => countryList.find(({ label }) => label === 'Hong Kong'),
    [countryList],
  );

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'success',
  });
  const [pageLoading, setPageLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogDetails, setDialogDetails] = useState<DialogDetails>({
    title: '',
  });

  const formikSetFieldValue = formik.setFieldValue;

  const handleUpdateMobileNumber = async (value: unknown): Promise<void> => {
    await formikSetFieldValue('mobileNumber', value);
  };

  const handleUpdateTelephone = async (value: unknown): Promise<void> => {
    await formikSetFieldValue('telephoneNumber', value);
  };

  const handleChangeNationality = async (
    event: React.ChangeEvent<{ value: unknown }>,
  ): Promise<void> => {
    const value = event.target.value as string;
    await formik.setValues({
      ...formik.values,
      nationality: value,
      identityDocumentType:
        value === CHN
          ? IDENTITY_DOCUMENT_TYPE.MAINLAND_ID
          : value === HKG && isPermanentHK
            ? IDENTITY_DOCUMENT_TYPE.HK_ID
            : IDENTITY_DOCUMENT_TYPE.PASSPORT,
    });
  };

  const handleChangeDisabilitySpecialType = async (
    event: React.ChangeEvent<{ value: unknown }>,
  ): Promise<void> => {
    const value = event.target.value as string;
    await formikSetFieldValue('disabilitySpecialType', value);
    await formikSetFieldValue('disabilitySpecialDetail', '');
  };

  // only activated  in super admin view
  const handleChangeVisaType = async (
    event: React.ChangeEvent<{ value: unknown }>,
  ): Promise<void> => {
    const identityType = event.target.value as string;
    await formikSetFieldValue('identityType', identityType);
  };

  const handleUpdateFormFields = async (): Promise<void> => {
    if (isPermanentHK && Boolean(hkOption?.value)) {
      await formikSetFieldValue('nationality', hkOption?.value);
    } else {
      await formikSetFieldValue('nationality', '');
    }
  };

  const visaActionMapping = {
    add: {
      dialogDetails: {
        title: PAGE_TEXT.VISA_COMPENSATION.ADD.DIALOG.TITLE,
        content: PAGE_TEXT.VISA_COMPENSATION.ADD.DIALOG.CONTENT,
        confirmAction: PAGE_TEXT.VISA_COMPENSATION.ADD.DIALOG.CONFIRM_ACTION,
        cancelAction: PAGE_TEXT.VISA_COMPENSATION.ADD.DIALOG.CANCEL_ACTION,
        handleDialogConfirmAction: async () => {
          await handleVisaCompensation('add');
        },
      },
      execute: regenerateVisa,
      reminderContent: PAGE_TEXT.VISA_COMPENSATION.ADD.REMINDER_CONTENT,
    },
    delete: {
      dialogDetails: {
        title: PAGE_TEXT.VISA_COMPENSATION.DELETE.DIALOG.TITLE,
        content: PAGE_TEXT.VISA_COMPENSATION.DELETE.DIALOG.CONTENT,
        confirmAction: PAGE_TEXT.VISA_COMPENSATION.DELETE.DIALOG.CONFIRM_ACTION,
        cancelAction: PAGE_TEXT.VISA_COMPENSATION.DELETE.DIALOG.CANCEL_ACTION,
        handleDialogConfirmAction: async () => {
          await handleVisaCompensation('delete');
        },
      },
      execute: deleteVisa,
      reminderContent: PAGE_TEXT.VISA_COMPENSATION.DELETE.REMINDER_CONTENT,
    },
    reminder: {
      dialogDetails: {
        title: PAGE_TEXT.VISA_COMPENSATION.REMINDER.DIALOG.TITLE,
        content: PAGE_TEXT.VISA_COMPENSATION.REMINDER.DIALOG.CONTENT,
        confirmAction:
          PAGE_TEXT.VISA_COMPENSATION.REMINDER.DIALOG.CONFIRM_ACTION,
        handleDialogConfirmAction: () => {
          setDialogOpen(false);
        },
      },
    },
  };

  const handleVisaCompensation = async (
    action: 'add' | 'delete',
  ): Promise<void> => {
    setDialogOpen(false);
    setPageLoading(true);

    const visaAction = visaActionMapping[action];
    const reminder = visaActionMapping.reminder;

    await visaAction
      .execute({
        variables: { applicantId: formik.values.id },
      })
      .then(res => {
        reminder.dialogDetails.content = visaAction.reminderContent;
        setDialogDetails(reminder.dialogDetails);
        setDialogOpen(true);
      })
      .catch(error => {
        setAlertDetails({
          alertContent: 'Error Occurs. ' + (error as Error)?.message,
          alertType: 'error',
        });
        setAlertOpen(true);
      })
      .finally(() => {
        setPageLoading(false);
      });
  };

  const handleVisaCompensationDialog = (action: 'add' | 'delete'): void => {
    setDialogDetails(visaActionMapping[action].dialogDetails);
    setDialogOpen(true);
  };

  // init
  const userInformationFieldMap: Array<
    InputTextProps<StepperFormContentValues> & {
      isPresent: boolean;
      fullWidth: boolean;
    }
  > = [
    {
      label: PAGE_TEXT.EMAIL,
      isPresent: false,
      type: 'text',
      disabled: true, // TODO currently system does not support to change the email once signed up
      fullWidth: false,
      required: true,
      name: 'email',
    },
    {
      label: PAGE_TEXT.SURNAME,
      isPresent: false,
      type: 'text',
      disabled: true,
      fullWidth: false,
      required: true,
      name: 'surname',
    },
    {
      label: PAGE_TEXT.GIVEN_NAME,
      isPresent: false,
      type: 'text',
      disabled: true,
      fullWidth: false,
      required: true,
      name: 'givenName',
    },
    {
      label: PAGE_TEXT.CHINESE_NAME,
      isPresent: false,
      type: 'text',
      disabled: true,
      fullWidth: false,
      required: false,
      name: 'chineseName',
    },
    {
      label: PAGE_TEXT.GENDER,
      isPresent: false,
      type: 'select',
      disabled: true,
      fullWidth: false,
      required: true,
      options: genderOptions,
      name: 'gender',
    },
    {
      label: PAGE_TEXT.BIRTHDAY,
      isPresent: false,
      type: 'date',
      disabled: true,
      fullWidth: false,
      required: true,
      name: 'dateOfBirth',
      minDate: null,
      maxDate: undefined,
    },
    {
      label: PAGE_TEXT.NATIONALITY,
      isPresent: false,
      type: 'select',
      options: countryList,
      disabled: true,
      fullWidth: false,
      required: true,
      name: 'nationality',
      handleChange: handleChangeNationality,
    },
    {
      label: PAGE_TEXT.PROVINCE,
      isPresent: false,
      type: 'select',
      options: provinceList,
      disabled: true,
      fullWidth: false,
      required: isChina,
      name: 'province',
    },
    {
      label: PAGE_TEXT.VISA_TYPE,
      isPresent: false,
      type: 'select',
      options: identityTypeFullOptions,
      disabled: true,
      fullWidth: true,
      required: true,
      name: 'identityType',
      handleChange: handleChangeVisaType,
      company: {
        name: 'identityTypeCompany',
        isPresent: false,
        disabled: true,
        component: function (isPresent: boolean, disabled: boolean) {
          return (
            isPresent && (
              <Stack direction="row" alignItems="center">
                <Button
                  className="!ml-4 !m-0 !px-4 !h-14 !normal-case"
                  variant="contained"
                  startIcon={<AddCircleOutlined />}
                  disabled={disabled}
                  onClick={() => {
                    handleVisaCompensationDialog('add');
                  }}
                >
                  {PAGE_TEXT.VISA_COMPENSATION.ADD.LABEL}
                </Button>
                <Tooltip title={PAGE_TEXT.VISA_COMPENSATION.ADD.HINTS}>
                  <span>
                    <IconButton
                      color="primary"
                      icon={<Help />}
                      className="!m-0"
                    />
                  </span>
                </Tooltip>
                <Button
                  className="!m-0 !px-4 !h-14 !normal-case"
                  variant="contained"
                  startIcon={<RemoveCircle />}
                  disabled={disabled}
                  onClick={() => {
                    handleVisaCompensationDialog('delete');
                  }}
                >
                  {PAGE_TEXT.VISA_COMPENSATION.DELETE.LABEL}
                </Button>
                <Tooltip title={PAGE_TEXT.VISA_COMPENSATION.DELETE.HINTS}>
                  <span>
                    <IconButton
                      color="primary"
                      icon={<Help />}
                      className="!m-0"
                    />
                  </span>
                </Tooltip>
              </Stack>
            )
          );
        },
      },
    },
    {
      label: PAGE_TEXT.IS_LOCAL,
      isPresent: false,
      type: 'select',
      options: localTypeOptions,
      disabled: true,
      fullWidth: false,
      required: false,
      name: 'isLocal',
    },
    {
      label: PAGE_TEXT.NEED_STUDENT_VISA,
      isPresent: false,
      type: 'select',
      options: needStudentVisaOptions,
      disabled: true,
      fullWidth: false,
      required: false,
      name: 'needStudentVisa',
    },
    {
      label:
        formik.values.nationality === CHN
          ? PAGE_TEXT.MAINLAND_IDENTITY_NUMBER
          : formik.values.nationality === HKG
            ? PAGE_TEXT.HK_IDENTITY_NUMBER
            : PAGE_TEXT.PASSPORT_NUMBER,
      isPresent: false,
      placeholder: formik.values.nationality === HKG ? 'A123456(7)' : '',
      type: 'text',
      disabled: true,
      fullWidth: true,
      required: true,
      name: 'identityDocumentNumber',
    },
    {
      label: PAGE_TEXT.ADDRESS_COUNTRY,
      isPresent: false,
      type: 'select',
      options: countryList,
      disabled: true,
      fullWidth: true,
      required: true,
      name: 'addressCountryRegion',
    },
    {
      label: PAGE_TEXT.ADDRESS_LINE_1,
      isPresent: false,
      type: 'text',
      disabled: true,
      fullWidth: true,
      required: true,
      name: 'addressLine1',
    },
    {
      label: PAGE_TEXT.ADDRESS_LINE_2,
      isPresent: false,
      type: 'text',
      disabled: true,
      fullWidth: true,
      required: false,
      name: 'addressLine2',
    },
    {
      label: PAGE_TEXT.ADDRESS_LINE_3,
      isPresent: false,
      type: 'text',
      disabled: true,
      fullWidth: true,
      required: false,
      name: 'addressLine3',
    },
    {
      label: PAGE_TEXT.MOBILE_PHONE_NUMBER,
      isPresent: false,
      type: 'phone',
      disabled: true,
      fullWidth: false,
      required: true,
      name: 'mobileNumber',
      handleChange: handleUpdateMobileNumber,
    },
    {
      label: PAGE_TEXT.HOME_PHONE_NUMBER,
      isPresent: false,
      type: 'phone',
      disabled: true,
      fullWidth: false,
      required: false,
      name: 'telephoneNumber',
      handleChange: handleUpdateTelephone,
    },
    {
      label: PAGE_TEXT.DISABILITY_SPECIAL_EDUCATIONAL_NEEDS,
      isPresent: false,
      type: 'default select',
      options: disabilityTypeList,
      disabled: true,
      fullWidth: true,
      required: false,
      name: 'disabilitySpecialType',
      handleChange: handleChangeDisabilitySpecialType,
    },
    {
      label: PAGE_TEXT.DISABILITY_SPECIAL_EDUCATIONAL_NEEDS_DETAIL,
      isPresent: false,
      type: 'textarea',
      disabled: true,
      fullWidth: true,
      required: false,
      name: 'disabilitySpecialDetail',
    },
    {
      label: (
        <div
          dangerouslySetInnerHTML={{
            __html: PAGE_TEXT.PI_DECLARATION,
          }}
        />
      ),
      isPresent: false,
      type: 'checkbox',
      disabled: true,
      fullWidth: true,
      required: false,
      name: 'PIDeclaration',
    },
  ];

  const preprocessFieldMap = (): void => {
    const APPLICANT_CONFIG = getApplicantConfig(viewMode, formik.values);
    userInformationFieldMap.forEach(field => {
      const config =
        APPLICANT_CONFIG[field.name as keyof StepperFormContentValues];
      field.isPresent = config.isPresent;
      field.disabled = config.disabled;
      field.fullWidth = config?.fullWidth ?? field.fullWidth;

      if (field.company) {
        const companyConfig =
          APPLICANT_CONFIG[
            field?.company?.name as keyof StepperFormContentValues
          ];
        field.company.isPresent = companyConfig.isPresent;
        field.company.disabled = companyConfig.disabled;
      }
    });
  };

  preprocessFieldMap();

  useEffect(() => {
    if (isSignUp) {
      // call the function
      handleUpdateFormFields()
        // make sure to catch any error
        .catch(console.error);
    }
  }, [isPermanentHK, hkOption?.value]);

  useEffect(() => {
    if (!isChina && isSignUp) {
      void formikSetFieldValue('province', '');
    }
  }, [isChina]);

  if (SRSError) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent: `Error Occurs. ` + (SRSError as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  return (
    <Container className={isSuperAdmin ? 'w-full' : 'w-3/4'}>
      {SRSLoading ? (
        <Loading />
      ) : (
        <Grid
          container
          size={{
            md: 12,
          }}
        >
          {userInformationFieldMap.map((field, index: number) => {
            const { isPresent, fullWidth, ...others } = field;

            const item: InputTextProps<StepperFormContentValues> = {
              ...others,
            };

            return (
              isPresent && (
                <React.Fragment key={index}>
                  <Grid
                    size={{
                      md: fullWidth ? 12 : 6,
                    }}
                  >
                    <>{returnedComponent({ item, formik })}</>
                  </Grid>
                </React.Fragment>
              )
            );
          })}
        </Grid>
      )}
      <Dialog
        openDialog={dialogOpen}
        dialogTitle={dialogDetails.title}
        dialogAction={dialogDetails.confirmAction}
        cancelAction={dialogDetails.cancelAction ?? undefined}
        onDialogAction={dialogDetails.handleDialogConfirmAction}
        onDialogClose={() => {
          setDialogOpen(false);
        }}
      >
        <Typography margin={'0rem 1.5rem 2rem'}>
          {dialogDetails.content}
        </Typography>
      </Dialog>
      <Alert
        openAlert={alertOpen}
        setOpenAlert={setAlertOpen}
        alertDetails={alertDetails}
      />
      {pageLoading && <Loading />}
    </Container>
  );
};

const Container = styled.div`
  padding: 0 3rem 3rem 3rem;

  form {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  .MuiGrid-container {
    margin-top: 0;
    margin-left: 0;

    width: calc(100%);
  }

  .MuiGrid-root .MuiGrid-item {
    margin-bottom: 0.5rem;
    display: flex;
    flex-direction: column;
    padding: 0.5rem 1rem;
  }

  .MuiInputBase-root {
    margin: 0 !important;
  }

  .MuiGrid-container {
    margin-top: 0;
    margin-left: 0;

    width: calc(100%);
  }
  .css-5jmw7p-MuiInputBase-root-MuiOutlinedInput-root {
    padding-right: 0rem;
  }

  .css-1yq5fb3-MuiButtonBase-root-MuiIconButton-root {
    padding: 0rem;
  }

  button {
    margin: 2rem;
  }
`;
